import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    AttributeDefinitionFieldType,
    AttributeValueState,
    AvailableEditionLanguages,
    ComplementaryExceptionLevelTypeState,
    ComplementaryHotelImageTypeState,
    ComplementaryLanguageState,
    ComplementaryPublishStatusState,
    ComplementaryServiceCategoryState,
    ComplementaryServiceTypeState,
    ComplementaryServiceAttributeState,
    ExceptionServiceState,
    HotelContentState,
    HotelImageState,
    SuggestedURLState,
    VisibleContentScope,
    ZinantisServiceState,
    ZinantisRoomServiceState,
} from '../types/zitoneoServicesTypes';
import { ZitoneoPreviewServiceCategoryState } from '../types/zitoneoPreviewTypes';
import {
    HOTEL_CONTENT_INITIAL_STATE,
    STATUS_KEYS,
    getAgencyConnectionStates,
    getNextPublishStatusKey,
    getSuggestedURLs,
    sortAttributeDefinitions,
} from '../utils/zitoneoServicesUtils';
import {
    getServiceTypesData,
    getServiceCategoriesData,
    getServiceAttributesData,
    getHotelImageTypes,
    getServiceAttributeDefinitions,
    saveZitoneoHotelContent,
    getSupplierHotelContentData,
    exportZitoneoHotelContentToExcel,
    getLanguagesData,
    changeStatus,
    getZitoneoPublishedStatusTypesData,
    getServiceExceptionLevelTypeData,
    getUsedZitoneoURLs,
    uploadZitoneoLogoImage,
} from '../services/zitoneoServices';
import { useNotification } from '../../../contexts/NotificationsContext';
import { useHistory, useLocation, matchPath } from 'react-router';
import { zitoneoRouteDefinitions, zitoneoRoutes } from '../utils/zitoneoRoutes';
import {
    adaptDDBBAttributeDefinitionToState,
    adaptDDBBHotelContentToState,
    adaptDDBBServiceTypeToState,
    adaptDDBBSupplierHotelContentToState,
} from '../services/adapters/zitoneoAdapters';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import { decodeBase64 } from '../../../utils/encodeBase64';
import { IServiceType } from '../../../types/entities/IServiceType';
import { IServiceCategory } from '../../../types/entities/IServiceCategory';
import { IHotelImageType } from '../../../types/entities/IHotelImageType';
import { downloadFiles, getRandomNumber } from '../../../utils/usefulFunctions';
import { useZitoneoUI } from './ZitoneoUIContext';
import { IContentLanguage } from '../../../types/entities/IContentLanguage';
import { IAttributeDefinition } from '../../../types/entities/IAttributeDefinition';
import useLanguage from '../../../customHooks/useLanguage';
import useUserProfile from '../../../customHooks/useUserProfile';
import { IZitoneoPublishStatusType } from '../../../types/entities/IZitoneoPublishStatusType';
import { IServiceExceptionLevelType } from '../../../types/entities/IServiceExceptionLevelType';
import { trackDependencyFailed } from '../../../appInsights';
import { dateIsInRange, getPastDate } from '../../../utils/formatDates';
import { IHotel } from '../../../types/entities/IHotel';

interface ZitoneoContentContextType {
    hotelContent: HotelContentState;
    hasUnsavedChanges: boolean;
    serviceTypes: Partial<
        Record<AvailableEditionLanguages, ComplementaryServiceTypeState[]>
    >;
    serviceCategories: Partial<
        Record<AvailableEditionLanguages, ComplementaryServiceCategoryState[]>
    >;
    hotelImageTypes: Partial<
        Record<AvailableEditionLanguages, ComplementaryHotelImageTypeState[]>
    >;
    serviceAttributes: Partial<
        Record<AvailableEditionLanguages, ComplementaryServiceAttributeState[]>
    >;
    languages: ComplementaryLanguageState[];
    handleAddService: (service: ZinantisServiceState) => void;
    handleAddRoomService: (
        idRoom: string,
        serviceType: ComplementaryServiceTypeState
    ) => Promise<string>;
    handleEditRoomServiceDescription: (
        idRoom: string,
        idService: string,
        description: string
    ) => void;
    saveHotelContent: (hotelData?: Partial<HotelContentState>) => void;
    applySavedHotelContent: (
        response: Partial<IHotel>,
        hotelData?: Partial<HotelContentState>
    ) => Partial<HotelContentState>;
    handleAddExceptionToService: (IdHotelService: string) => void;
    editExceptionInService: (
        IdHotelService: string,
        exception: ExceptionServiceState
    ) => void;
    handleEditExceptionDescription: (
        IdHotelService: string,
        IdException: string,
        value: string
    ) => void;
    handleEditServiceContent: (
        IdHotelService: string,
        field: 'Name' | 'Description',
        value: string
    ) => void;
    handleDeleteService: (IdHotelService: string) => void;
    handleChangeAttributeValue: (
        idService: string,
        idAttributeDefinition: number,
        idValue: string,
        newValue: string
    ) => void;
    handleDeleteAttributeValue: (idService: string, idValue: string) => void;
    handleChangeRoomServiceAttributeValue: (
        idRoom: string,
        idService: string,
        idAttributeDefinition: number,
        idValue: string,
        newValue: string
    ) => void;
    handleDeleteRoomServiceAttributeValue: (
        idRoom: string,
        idService: string,
        idValue: string
    ) => void;
    handleDeleteRoomService: (idRoom: string, idService: string) => void;
    handleDeleteException: (
        IdHotelService: string,
        IdException: string
    ) => void;
    handleDownloadExcel: () => void;
    handleChangeStatus: () => void;
    criticalExceptionLevelType: ComplementaryExceptionLevelTypeState | null;
    editUrl: (urlId: string, key: string, newValue: string | boolean) => void;
    usedURLPaths: string[];
    suggestedURLs: SuggestedURLState[];
    addUrl: (url: string) => void;
    uploadLogoImageToDDBB: (file: File) => Promise<void>;
    uploadIconImageToDDBB: (file: File) => Promise<void>;
    handleAddHotelImages: (newImages: HotelImageState[]) => void;
    handleUpdateHotelImage: (
        imageId: string,
        changes: Partial<
            Pick<
                HotelImageState,
                'Caption' | 'Feature' | 'Published' | 'Deleted'
            >
        >,
        roomId?: string | null
    ) => void;
}

export interface RequiredAttributeMissingState {
    IdHotelService: string;
    IdServiceType: number;
    ServiceType: string;
    InstanceName: string;
    Definition: NonNullable<
        ZinantisServiceState['AttributeDefinitions']
    >[number];
}

const ZitoneoContentContext = createContext<
    ZitoneoContentContextType | undefined
>(undefined);

export const ProviderZitoneoContent = ({
    children,
}: {
    children: ReactNode;
}) => {
    const appLanguage = useLanguage();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { IdSystemUser, username } = useUserProfile();
    const {
        handleVisibleServicesDetail,
        idVisibleServicesDetail,
        handleVisibleLanguage,
        visibleLanguage,
    } = useZitoneoUI();
    const history = useHistory();
    const { pathname } = useLocation();
    const { handleLoading } = useZitoneoUI();

    const match = matchPath<{ idEncoded: string }>(pathname, {
        path: zitoneoRouteDefinitions.HOTEL_PRESENTATION,
    });
    const idEncoded = match?.params?.idEncoded;
    const idSupplierHotel = idEncoded ? decodeBase64(idEncoded) : null;
    const [hotelContent, setHotelContent] = useState<HotelContentState>(
        HOTEL_CONTENT_INITIAL_STATE
    );
    const savedHotelContentRef = useRef<HotelContentState>(
        HOTEL_CONTENT_INITIAL_STATE
    );

    const handleAddHotelImages = (newImages: HotelImageState[]) => {
        setHotelContent(previous => {
            return {
                ...previous,
                HotelImages: [...previous.HotelImages, ...newImages],
                HotelRoomTypes: previous.HotelRoomTypes.map(room => ({
                    ...room,
                    HotelImages: [
                        ...room.HotelImages,
                        ...newImages.filter(
                            image => image.IdHotelRoomType === Number(room.Id)
                        ),
                    ],
                    __isChanged: true,
                })),
            };
        });
    };

    const handleUpdateHotelImage = (
        imageId: string,
        changes: Partial<
            Pick<
                HotelImageState,
                'Caption' | 'Feature' | 'Published' | 'Deleted'
            >
        >,
        roomId?: string | null
    ) => {
        setHotelContent(previous => {
            return {
                ...previous,
                HotelImages: previous.HotelImages.map(image => {
                    const isTarget = !roomId && image.Id === imageId;
                    return {
                        ...image,
                        ...(changes.Feature === true
                            ? { Feature: isTarget }
                            : {}),
                        ...(isTarget ? changes : {}),
                        __isChanged:
                            image.__isChanged ||
                            isTarget ||
                            (changes.Feature === true && image.Feature),
                    };
                }),
                HotelRoomTypes: previous.HotelRoomTypes.map(room => ({
                    ...room,
                    HotelImages: room.HotelImages.map(image => {
                        const isTarget =
                            room.Id === roomId && image.Id === imageId;
                        return {
                            ...image,
                            ...(changes.Feature === true
                                ? { Feature: isTarget }
                                : {}),
                            ...(isTarget ? changes : {}),
                            __isChanged:
                                image.__isChanged ||
                                isTarget ||
                                (changes.Feature === true && image.Feature),
                        };
                    }),
                })),
            };
        });
    };
    const [serviceTypesMap, setServiceTypesMap] = useState<
        Partial<
            Record<AvailableEditionLanguages, ComplementaryServiceTypeState[]>
        >
    >({});
    const [serviceCategoriesMap, setServiceCategoriesMap] = useState<
        Partial<
            Record<
                AvailableEditionLanguages,
                ComplementaryServiceCategoryState[]
            >
        >
    >({});
    const [hotelImageTypesMap, setHotelImageTypesMap] = useState<
        Partial<
            Record<
                AvailableEditionLanguages,
                ComplementaryHotelImageTypeState[]
            >
        >
    >({});
    const [languages, setLanguages] = useState<ComplementaryLanguageState[]>(
        []
    );
    const [serviceAttributesMap, setServiceAttributesMap] = useState<
        Partial<
            Record<
                AvailableEditionLanguages,
                ComplementaryServiceAttributeState[]
            >
        >
    >({});

    const [publishStatuses, setPublishStatuses] = useState<
        ComplementaryPublishStatusState[]
    >([]);

    const [criticalExceptionLevelType, setCriticalExceptionLevelType] =
        useState<ComplementaryExceptionLevelTypeState | null>(null);

    const [usedURLPaths, setUsedURLPaths] = useState<string[]>([]);

    const suggestedURLs = getSuggestedURLs(
        hotelContent.Name,
        hotelContent.City,
        hotelContent.IdHotel,
        usedURLPaths,
        hotelContent.URLs.filter(u => !u.Deleted).map(u => u.Path)
    );

    const getUsedURLs = async () => {
        try {
            const data = await getUsedZitoneoURLs(hotelContent.IdHotel);
            setUsedURLPaths(
                data.value.map((url: { Path: string }) => url.Path)
            );
        } catch (error) {
            console.error(error);
        }
    };

    const getHotelContent = async (idSuppHotel: string) => {
        try {
            handleLoading(true);
            const { data } = await getSupplierHotelContentData(idSuppHotel);

            if (!data.Success && data.Messages && data.Messages.length) {
                setErrorMessage(data.Messages[0]?.Title);
                history.push(zitoneoRoutes.HOTELS_CONTENT_LIST);
                return;
            }
            if (!data.Result?.Hotel?.ZitoneoMembership) {
                setErrorMessage(FeedbackMessages.ZITONEO_NOT_MEMBER);
                history.push(zitoneoRoutes.HOTELS_CONTENT_LIST);
                return;
            }
            const adaptedSupplierHotelContent =
                adaptDDBBSupplierHotelContentToState(data.Result);
            const mergedHotelContent: HotelContentState = {
                ...HOTEL_CONTENT_INITIAL_STATE,
                ...hotelContent,
                ...adaptedSupplierHotelContent,
            };
            savedHotelContentRef.current = mergedHotelContent;
            setHotelContent(prevContent => ({
                ...prevContent,
                ...adaptedSupplierHotelContent,
            }));
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        } finally {
            handleLoading(false);
        }
    };

    const getServiceTypes = async (lang: string) => {
        try {
            const data = await getServiceTypesData(lang);
            setServiceTypesMap(prev => ({
                ...prev,
                [lang]: data.value
                    .map((item: IServiceType) => {
                        const serviceDateCreated = new Date(item.DateCreated);
                        const twoMonthsAgo = getPastDate(2, 'month');
                        const isNewService = dateIsInRange(serviceDateCreated, {
                            start: twoMonthsAgo,
                            end: new Date(),
                        });
                        return {
                            Id: String(item.Id),
                            Name:
                                item.LocalizedCaption ||
                                item.Caption ||
                                item.Name,
                            IdServiceCategory: item.IdServiceCategory,
                            IsNewService: isNewService,
                            ForRoom: item.ForRoomType,
                            ForHotel: item.ForHotel,
                        };
                    })
                    .sort(
                        (
                            a: ComplementaryServiceTypeState,
                            b: ComplementaryServiceTypeState
                        ) => a.Name.localeCompare(b.Name)
                    ),
            }));
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    const getServiceCategories = async (lang: string) => {
        try {
            const data = await getServiceCategoriesData(lang);
            setServiceCategoriesMap(prev => ({
                ...prev,
                [lang]: data.value
                    .map((item: IServiceCategory) => ({
                        Id: String(item.Id),
                        Name:
                            item.LocalizedCaption || item.Caption || item.Name,
                        Key: item.Key,
                        Description:
                            item.LocalizedDescription || item.Description || '',
                        Icon: item.Icon,
                        ForRoom: item.ForRoomType,
                        ForHotel: item.ForHotel,
                    }))
                    .sort(
                        (
                            a: ComplementaryServiceCategoryState,
                            b: ComplementaryServiceCategoryState
                        ) => a.Name.localeCompare(b.Name)
                    ),
            }));
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    const getServiceAttributes = async (lang: string) => {
        try {
            const data = await getServiceAttributesData(lang);
            setServiceAttributesMap(prev => ({
                ...prev,
                [lang]: data.value
                    .map((item: IAttributeDefinition) => ({
                        Id: String(item.Id),
                        Name: item.LocalizedName || item.Name,
                        Key: item.Key,
                        IsBoolean: item.FieldType?.IsBoolean || false,
                        IsDate: item.FieldType?.IsDate || false,
                        IsNumeric: item.FieldType?.IsNumeric || false,
                        IsString: item.FieldType?.IsString || false,
                    }))
                    .sort(
                        (
                            a: ComplementaryServiceAttributeState,
                            b: ComplementaryServiceAttributeState
                        ) => a.Name.localeCompare(b.Name)
                    ),
            }));
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    const getImageTypes = async (lang: string) => {
        try {
            const data = await getHotelImageTypes(lang);
            setHotelImageTypesMap(prev => ({
                ...prev,
                [lang]: data.value
                    .map((item: IHotelImageType) => ({
                        Id: String(item.Id),
                        Name:
                            item.LocalizedCaption || item.Caption || item.Name,
                        Key: item.Key,
                        Icon: item.Icon,
                    }))
                    .sort(
                        (
                            a: ComplementaryHotelImageTypeState,
                            b: ComplementaryHotelImageTypeState
                        ) => a.Name.localeCompare(b.Name)
                    ),
            }));
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    const getLanguages = async () => {
        try {
            const data = await getLanguagesData();
            const mapped = data.value.map((item: IContentLanguage) => ({
                Id: item.Id,
                Name: item.LocalizedCaption || item.Caption || item.Name,
                Code: item.CultureId,
            }));
            setLanguages(mapped);
            if (!visibleLanguage) {
                const english = mapped.find(
                    (lang: ComplementaryLanguageState) => lang.Code === 'en'
                );
                const appLang = mapped.find(
                    (lang: ComplementaryLanguageState) =>
                        lang.Code === appLanguage
                );
                if (english) {
                    handleVisibleLanguage(english);
                } else if (appLang) {
                    handleVisibleLanguage(appLang);
                } else {
                    handleVisibleLanguage(mapped[0]);
                }
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    const getZitoneoPublishedStatusType = async () => {
        try {
            const data = await getZitoneoPublishedStatusTypesData();
            const mapped = data.value.map(
                (item: IZitoneoPublishStatusType) => ({
                    Id: item.Id,
                    Name: item.LocalizedCaption || item.Caption || item.Name,
                    Key: item.Key,
                })
            );
            setPublishStatuses(mapped);
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    const getZitoneoExceptionLevelType = async () => {
        try {
            const data = await getServiceExceptionLevelTypeData();
            const mapped = data.value.map(
                (item: IServiceExceptionLevelType) => ({
                    Id: item.Id,
                    Name: item.LocalizedCaption || item.Caption || item.Name,
                    Key: item.Key,
                })
            );
            setCriticalExceptionLevelType(mapped[0] || null);
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        }
    };

    useEffect(() => {
        getLanguages();
        getZitoneoPublishedStatusType();
        getZitoneoExceptionLevelType();
    }, []);

    useEffect(() => {
        const langsToLoad = new Set<AvailableEditionLanguages>();

        if (visibleLanguage?.Code) {
            langsToLoad.add(visibleLanguage.Code as AvailableEditionLanguages);
        }

        if (appLanguage) {
            langsToLoad.add(appLanguage as AvailableEditionLanguages);
        }

        langsToLoad.forEach(lang => {
            if (!serviceTypesMap[lang]) {
                getServiceTypes(lang);
            }
            if (!serviceCategoriesMap[lang]) {
                getServiceCategories(lang);
            }
            if (!hotelImageTypesMap[lang]) {
                getImageTypes(lang);
            }
            if (!serviceAttributesMap[lang]) {
                getServiceAttributes(lang);
            }
        });
    }, [visibleLanguage?.Code, appLanguage]);

    useEffect(() => {
        if (idSupplierHotel) {
            getHotelContent(idSupplierHotel);
            getUsedURLs();
        }
    }, [idSupplierHotel]);

    const buildDefaultBooleanValues = (
        attributeDefinitions?: ZinantisServiceState['AttributeDefinitions'],
        existingValues: AttributeValueState[] = []
    ): AttributeValueState[] => {
        const definitions = attributeDefinitions || [];

        const missingBooleanValues = definitions
            .filter(
                definition =>
                    definition.FieldType ===
                        AttributeDefinitionFieldType.BOOLEAN &&
                    !existingValues.some(
                        value =>
                            value.IdAttributeDefinition ===
                            definition.IdAttributeDefinition
                    )
            )
            .map(definition => ({
                Id: `-${getRandomNumber()}`,
                IdAttributeDefinition: definition.IdAttributeDefinition,
                Value: 'False',
                ContentTranslations: [],
                IsString: false,
                __isAdded: true,
                __isChanged: true,
            }));

        return [...existingValues, ...missingBooleanValues];
    };

    const handleAddService = async (service: ZinantisServiceState) => {
        const sameServiceType = hotelContent.HotelServices.find(
            s => s.IdServiceType === service.IdServiceType
        );
        const reusedDefinitions = sameServiceType?.AttributeDefinitions
            ? [...sameServiceType.AttributeDefinitions]
            : undefined;
        const baseService: ZinantisServiceState = {
            ...service,
            AttributeDefinitions: reusedDefinitions,
            AttributeValues: buildDefaultBooleanValues(reusedDefinitions),
            __isAdded: true,
            __isChanged: true,
        };

        // Add base service first for immediate UI feedback.
        setHotelContent(prevContent => ({
            ...prevContent,
            HotelServices: [...prevContent.HotelServices, baseService],
        }));

        if (!(sameServiceType && sameServiceType.AttributeDefinitions)) {
            try {
                const { data } = await getServiceAttributeDefinitions(
                    service.IdServiceType
                );
                if (data.Success && data.Result) {
                    const serviceAdditionalInto = adaptDDBBServiceTypeToState(
                        data.Result
                    );
                    const mappedDefinitions = sortAttributeDefinitions(
                        data.Result?.AttributeDefinitionEntities.map(attrDef =>
                            adaptDDBBAttributeDefinitionToState(attrDef)
                        ) || []
                    );
                    setHotelContent(prev => {
                        return {
                            ...prev,
                            HotelServices: prev.HotelServices.map(prevService =>
                                prevService.Id === service.Id
                                    ? {
                                          ...prevService,
                                          ...serviceAdditionalInto,
                                          AttributeDefinitions:
                                              mappedDefinitions,
                                          AttributeValues:
                                              buildDefaultBooleanValues(
                                                  mappedDefinitions,
                                                  prevService.AttributeValues ||
                                                      []
                                              ),
                                          __isChanged: true,
                                      }
                                    : prevService
                            ),
                        };
                    });
                } else {
                    setHotelContent(prev => ({
                        ...prev,
                        HotelServices: prev.HotelServices.filter(
                            prevService => prevService.Id !== service.Id
                        ),
                    }));
                    setErrorMessage(FeedbackMessages.ERROR_FETCHING);
                }
            } catch (error) {
                setHotelContent(prev => ({
                    ...prev,
                    HotelServices: prev.HotelServices.filter(
                        prevService => prevService.Id !== service.Id
                    ),
                }));
                setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            }
        }
        if (idVisibleServicesDetail !== service.IdServiceType) {
            handleVisibleServicesDetail(service.IdServiceType);
        }
    };

    const handleAddRoomService = async (
        idRoom: string,
        serviceType: ComplementaryServiceTypeState
    ) => {
        const roomService: ZinantisRoomServiceState = {
            Id: `-${getRandomNumber()}`,
            IdServiceType: parseInt(serviceType.Id),
            IdServiceCategory: serviceType.IdServiceCategory,
            ServiceType: serviceType.Name,
            ServiceCategory: '',
            Description: '',
            AttributeDefinitions: undefined,
            AttributeValues: [],
            __isAdded: true,
            __isChanged: true,
        };

        setHotelContent(prev => ({
            ...prev,
            HotelRoomTypes: prev.HotelRoomTypes.map(room =>
                room.Id === idRoom
                    ? {
                          ...room,
                          Services: [...room.Services, roomService],
                          __isChanged: true,
                      }
                    : room
            ),
        }));

        try {
            const { data } = await getServiceAttributeDefinitions(
                roomService.IdServiceType
            );
            if (data.Success && data.Result) {
                const definitions = sortAttributeDefinitions(
                    data.Result.AttributeDefinitionEntities.map(attrDef =>
                        adaptDDBBAttributeDefinitionToState(attrDef)
                    )
                );
                setHotelContent(prev => ({
                    ...prev,
                    HotelRoomTypes: prev.HotelRoomTypes.map(room =>
                        room.Id === idRoom
                            ? {
                                  ...room,
                                  Services: room.Services.map(service =>
                                      service.Id === roomService.Id
                                          ? {
                                                ...service,
                                                AttributeDefinitions:
                                                    definitions,
                                                AttributeValues:
                                                    buildDefaultBooleanValues(
                                                        definitions
                                                    ),
                                                __isChanged: true,
                                            }
                                          : service
                                  ),
                              }
                            : room
                    ),
                }));
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
        }

        return roomService.Id;
    };

    const handleEditRoomServiceDescription = (
        idRoom: string,
        idService: string,
        description: string
    ) => {
        setHotelContent(prev => ({
            ...prev,
            HotelRoomTypes: prev.HotelRoomTypes.map(room =>
                room.Id !== idRoom
                    ? room
                    : {
                          ...room,
                          __isChanged: true,
                          Services: room.Services.map(service => {
                              if (service.Id !== idService) return service;
                              return {
                                  ...service,
                                  Description: description,
                                  __isChanged: true,
                              };
                          }),
                      }
            ),
        }));
    };

    const handleAddExceptionToService = (IdHotelService: string) => {
        const newException: ExceptionServiceState = {
            Id: `-${getRandomNumber()}`,
            IdHotelService: parseInt(IdHotelService),
            Active: true,
            Description: '',
            IdServiceExceptionLevelType: 0,
            Dates: [
                {
                    Id: `-${getRandomNumber()}`,
                    Active: true,
                    FromDate: new Date(),
                    ToDate: '',
                    __isAdded: true,
                },
            ],
            __isAdded: true,
        };
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service => {
                if (service.Id !== IdHotelService) return service;
                return {
                    ...service,
                    Exceptions: [...service.Exceptions, newException],
                    __isChanged: true,
                };
            }),
        }));
    };

    const editExceptionInService = (
        IdHotelService: string,
        exception: ExceptionServiceState
    ) => {
        setHotelContent(prev => {
            const updatedServices = prev.HotelServices.map(service => {
                if (service.Id === IdHotelService) {
                    const updatedExceptions = service.Exceptions.map(ex => {
                        if (ex.Id === exception.Id) {
                            return { ...exception, __isChanged: true };
                        }
                        return ex;
                    });
                    return {
                        ...service,
                        Exceptions: updatedExceptions,
                        __isChanged: true,
                    };
                }
                return service;
            });
            return {
                ...prev,
                HotelServices: updatedServices,
            };
        });
    };

    const handleEditExceptionDescription = (
        IdHotelService: string,
        IdException: string,
        value: string
    ) => {
        if (!visibleLanguage) return;
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service => {
                if (service.Id !== IdHotelService) return service;
                return {
                    ...service,
                    Exceptions: service.Exceptions.map(ex => {
                        if (ex.Id !== IdException) return ex;

                        return {
                            ...ex,
                            Description: value,
                            __isChanged: true,
                        };
                    }),
                    __isChanged: true,
                };
            }),
        }));
    };

    const handleEditServiceContent = (
        IdHotelService: string,
        field: 'Name' | 'Description',
        value: string
    ) => {
        if (!visibleLanguage) return;
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service => {
                if (service.Id !== IdHotelService) return service;

                return {
                    ...service,
                    __isChanged: true,
                    [field]: value,
                };
            }),
        }));
    };

    const handleDeleteException = (
        IdHotelService: string,
        IdException: string
    ) => {
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service => {
                if (service.Id !== IdHotelService) return service;
                const exception = service.Exceptions.find(
                    ex => ex.Id === IdException
                );
                if (!exception) return service;
                let updatedExceptions;
                if (exception.__isAdded) {
                    updatedExceptions = service.Exceptions.filter(
                        ex => ex.Id !== IdException
                    );
                } else {
                    updatedExceptions = service.Exceptions.map(ex =>
                        ex.Id === IdException
                            ? { ...ex, Deleted: true, __isChanged: true }
                            : ex
                    );
                }
                return {
                    ...service,
                    Exceptions: updatedExceptions,
                    __isChanged: true,
                };
            }),
        }));
    };

    const handleDeleteService = (IdHotelService: string) => {
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service =>
                service.Id === IdHotelService
                    ? { ...service, Deleted: true, __isChanged: true }
                    : service
            ),
        }));
    };

    const handleChangeAttributeValue = (
        idService: string,
        idAttributeDefinition: number,
        idValue: string,
        newValue: string
    ) => {
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service => {
                if (service.Id !== idService) return service;

                const existingValue = (service.AttributeValues || []).find(
                    attributeValue => attributeValue.Id === idValue
                );

                const isString = existingValue
                    ? existingValue.IsString
                    : service.AttributeDefinitions?.find(
                          def =>
                              def.IdAttributeDefinition ===
                              idAttributeDefinition
                      )?.FieldType === AttributeDefinitionFieldType.STRING;

                let updatedValues: typeof service.AttributeValues;

                if (existingValue) {
                    updatedValues = (service.AttributeValues || []).map(
                        attributeValue =>
                            attributeValue.Id === idValue
                                ? {
                                      ...attributeValue,
                                      Value: newValue,
                                      __isChanged: true,
                                  }
                                : attributeValue
                    );
                } else {
                    updatedValues = [
                        ...(service.AttributeValues || []),
                        {
                            Id: idValue,
                            IdAttributeDefinition: idAttributeDefinition,
                            Value: newValue,
                            IsString: isString,
                            __isAdded: true,
                        },
                    ];
                }

                return {
                    ...service,
                    AttributeValues: updatedValues,
                    __isChanged: true,
                };
            }),
        }));
    };

    const handleDeleteAttributeValue = (idService: string, idValue: string) => {
        setHotelContent(prev => ({
            ...prev,
            HotelServices: prev.HotelServices.map(service => {
                if (service.Id !== idService) return service;
                const value = (service.AttributeValues || []).find(
                    attributeValue => attributeValue.Id === idValue
                );
                if (!value) return service;
                let updatedValues;
                if (value.__isAdded) {
                    updatedValues = (service.AttributeValues || []).filter(
                        attributeValue => attributeValue.Id !== idValue
                    );
                } else {
                    updatedValues = (service.AttributeValues || []).map(
                        attributeValue =>
                            attributeValue.Id === idValue
                                ? {
                                      ...attributeValue,
                                      Deleted: true,
                                      __isChanged: true,
                                  }
                                : attributeValue
                    );
                }
                return {
                    ...service,
                    AttributeValues: updatedValues,
                    __isChanged: true,
                };
            }),
        }));
    };

    const handleChangeRoomServiceAttributeValue = (
        idRoom: string,
        idService: string,
        idAttributeDefinition: number,
        idValue: string,
        newValue: string
    ) => {
        if (!visibleLanguage) return;
        setHotelContent(prev => ({
            ...prev,
            HotelRoomTypes: prev.HotelRoomTypes.map(room =>
                room.Id !== idRoom
                    ? room
                    : {
                          ...room,

                          Services: room.Services.map(service =>
                              service.Id !== idService
                                  ? service
                                  : {
                                        ...service,
                                        AttributeValues: (() => {
                                            const values =
                                                service.AttributeValues || [];
                                            const definition =
                                                service.AttributeDefinitions?.find(
                                                    item =>
                                                        item.IdAttributeDefinition ===
                                                        idAttributeDefinition
                                                );
                                            const isString =
                                                definition?.FieldType ===
                                                AttributeDefinitionFieldType.STRING;
                                            const existingValue = values.find(
                                                value => value.Id === idValue
                                            );

                                            if (!existingValue) {
                                                return [
                                                    ...values,
                                                    {
                                                        Id: idValue,
                                                        IdAttributeDefinition:
                                                            idAttributeDefinition,
                                                        Value: newValue,

                                                        IsString: isString,
                                                        __isAdded: true,
                                                        __isChanged: true,
                                                    },
                                                ];
                                            }

                                            return values.map(value =>
                                                value.Id === idValue
                                                    ? {
                                                          ...value,
                                                          Value: newValue,
                                                          __isChanged: true,
                                                      }
                                                    : value
                                            );
                                        })(),
                                        __isChanged: true,
                                    }
                          ),
                          __isChanged: true,
                      }
            ),
        }));
    };

    const handleDeleteRoomServiceAttributeValue = (
        idRoom: string,
        idService: string,
        idValue: string
    ) => {
        setHotelContent(prev => ({
            ...prev,
            HotelRoomTypes: prev.HotelRoomTypes.map(room =>
                room.Id !== idRoom
                    ? room
                    : {
                          ...room,
                          __isChanged: true,
                          Services: room.Services.map(service =>
                              service.Id !== idService
                                  ? service
                                  : {
                                        ...service,
                                        AttributeValues: (() => {
                                            const values =
                                                service.AttributeValues || [];
                                            const value = values.find(
                                                attributeValue =>
                                                    attributeValue.Id ===
                                                    idValue
                                            );
                                            if (value?.__isAdded) {
                                                return values.filter(
                                                    attributeValue =>
                                                        attributeValue.Id !==
                                                        idValue
                                                );
                                            }
                                            return values.map(valueItem =>
                                                valueItem.Id === idValue
                                                    ? {
                                                          ...valueItem,
                                                          Deleted: true,
                                                          __isChanged: true,
                                                      }
                                                    : valueItem
                                            );
                                        })(),
                                        __isChanged: true,
                                    }
                          ),
                      }
            ),
        }));
    };

    const handleDeleteRoomService = (idRoom: string, idService: string) => {
        setHotelContent(prev => ({
            ...prev,
            HotelRoomTypes: prev.HotelRoomTypes.map(room =>
                room.Id !== idRoom
                    ? room
                    : {
                          ...room,
                          Services: room.Services.map(service =>
                              service.Id === idService
                                  ? {
                                        ...service,
                                        Deleted: true,
                                        __isChanged: true,
                                    }
                                  : service
                          ),
                          __isChanged: true,
                      }
            ),
        }));
    };

    const addUrl = (url: string) => {
        setHotelContent(prev => ({
            ...prev,
            URLs: [
                ...prev.URLs,
                {
                    Id: `-${getRandomNumber()}`,
                    Path: url,
                    Enabled: !prev.URLs.filter(u => !u.Deleted).length,
                    IsPrimary: !prev.URLs.filter(u => !u.Deleted).length,
                    __isAdded: true,
                },
            ],
        }));
    };

    const editUrl = (
        idUrl: string,
        key: string,
        newValue: string | boolean
    ) => {
        setHotelContent(prev => ({
            ...prev,
            URLs: prev.URLs.map(url => {
                if (url.Id !== idUrl) {
                    if (key === 'IsPrimary' && newValue === true) {
                        return {
                            ...url,
                            IsPrimary: false,
                            __isChanged: true,
                        };
                    }
                    return url;
                }
                return {
                    ...url,
                    [key]: newValue,
                    __isChanged: true,
                };
            }),
        }));
    };

    const applySavedHotelContent = (
        response: Partial<IHotel>,
        hotelData: Partial<HotelContentState> = {}
    ) => {
        const adaptedHotelContent = adaptDDBBHotelContentToState(response);
        const mergedHotelContent: HotelContentState = {
            ...HOTEL_CONTENT_INITIAL_STATE,
            ...hotelContent,
            ...hotelData,
            ...adaptedHotelContent,
        };
        savedHotelContentRef.current = mergedHotelContent;
        setHotelContent(prevContent => ({
            ...prevContent,
            ...adaptedHotelContent,
        }));
        return adaptedHotelContent;
    };

    const saveHotelContent = async (
        hotelData: Partial<HotelContentState> = hotelContent
    ) => {
        try {
            handleLoading(true);
            const res = await saveZitoneoHotelContent(
                { ...hotelContent, ...hotelData },
                parseInt(IdSystemUser)
            );
            if (res.data && res.status === 201) {
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
                const adaptedHotelContent = applySavedHotelContent(
                    res.data,
                    hotelData
                );
                // If the status was "Not Started", we change it to the next status after saving.
                if (
                    adaptedHotelContent.ZitoneoPublishStatusKey ===
                    STATUS_KEYS.NOT_STARTED
                ) {
                    handleChangeStatus();
                }
            } else {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
                trackDependencyFailed({
                    id: crypto.randomUUID(),
                    name: 'saveZitoneoHotelContent',
                    data: '/api/hotelowner/save',
                    responseCode: res.status,
                    type: 'HTTP',
                    properties: {
                        data: JSON.stringify(res.data ?? res),
                        idSupplierHotel: idSupplierHotel,
                    },
                });
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            console.error(error);
        } finally {
            handleLoading(false);
        }
    };

    const uploadImageToDDBB = async (file: File, type: 'logo' | 'icon') => {
        try {
            handleLoading(true);
            const uploadFile =
                type === 'icon' ? await convertToPNG(file) : file;
            const { data } = await uploadZitoneoLogoImage(uploadFile);
            const uploadedFile = data?.Result?.files?.[0];

            if (!data?.Success || !uploadedFile) {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
                return;
            }

            const fileState = {
                Caption: uploadedFile.Caption || file.name,
                VirtualPath: uploadedFile.VirtualPath || '',
            };
            const nextHotelContent = {
                ...hotelContent,
                ...(type === 'logo'
                    ? {
                          IdLogoFile: parseInt(uploadedFile.Id),
                          LogoFile: fileState,
                      }
                    : {
                          IdIconFile: parseInt(uploadedFile.Id),
                          IconFile: fileState,
                      }),
            };

            setHotelContent(nextHotelContent);
            await saveHotelContent(nextHotelContent);
            setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_CREATE);

            if (data.Result.failed?.length) {
                setErrorMessage(data.Result.failed[0]);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            console.error(error);
        } finally {
            handleLoading(false);
        }
    };

    const uploadLogoImageToDDBB = (file: File) =>
        uploadImageToDDBB(file, 'logo');

    const convertToPNG = async (file: File): Promise<File> => {
        const imageUrl = URL.createObjectURL(file);

        try {
            const image = await new Promise<HTMLImageElement>(
                (resolve, reject) => {
                    const img = new Image();
                    img.onload = () => resolve(img);
                    img.onerror = () => reject(new Error('Invalid image'));
                    img.src = imageUrl;
                }
            );

            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;

            const context = canvas.getContext('2d');

            if (!context) {
                throw new Error('Could not create canvas context');
            }

            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            const pngBlob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(blob => {
                    if (!blob) {
                        reject(new Error('Could not convert icon to PNG'));
                        return;
                    }
                    resolve(blob);
                }, 'image/png');
            });

            const fileName = file.name.replace(/\.[^/.]+$/, '') || 'icon';

            return new File([pngBlob], `${fileName}.png`, {
                type: 'image/png',
                lastModified: Date.now(),
            });
        } finally {
            URL.revokeObjectURL(imageUrl);
        }
    };

    const uploadIconImageToDDBB = (file: File) =>
        uploadImageToDDBB(file, 'icon');

    const handleDownloadExcel = async () => {
        try {
            handleLoading(true);
            const { data } = await exportZitoneoHotelContentToExcel(
                hotelContent.Id
            );
            if (data && data.Success) {
                downloadFiles(data.Result, `HotelContent`, 'xlsx');
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            console.error(error);
        } finally {
            handleLoading(false);
        }
    };

    const handleChangeStatus = async () => {
        const newStatusKey = getNextPublishStatusKey(
            hotelContent.ZitoneoPublishStatusKey
        );
        const statusToChange = publishStatuses.find(
            status => status.Key === newStatusKey
        );
        if (!statusToChange) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            return;
        }
        try {
            handleLoading(true);
            const { data } = await changeStatus(
                hotelContent.IdHotel,
                newStatusKey,
                username
            );
            if (data && data.Success) {
                setHotelContent(prev => ({
                    ...prev,
                    ZitoneoPublishStatus: statusToChange.Name,
                    ZitoneoPublishStatusKey: statusToChange.Key,
                }));
                setSuccessMessage(
                    FeedbackMessages.ZITONEO_SUCCESSFUL_STATUS_CHANGE
                );
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            console.error(error);
        } finally {
            handleLoading(false);
        }
    };

    const hasUnsavedChanges =
        JSON.stringify(hotelContent) !==
        JSON.stringify(savedHotelContentRef.current);

    return (
        <ZitoneoContentContext.Provider
            value={{
                hotelContent,
                hasUnsavedChanges,
                serviceTypes: serviceTypesMap,
                serviceCategories: serviceCategoriesMap,
                hotelImageTypes: hotelImageTypesMap,
                serviceAttributes: serviceAttributesMap,
                languages,
                handleAddService,
                handleAddRoomService,
                handleEditRoomServiceDescription,
                saveHotelContent,
                applySavedHotelContent,
                handleAddExceptionToService,
                editExceptionInService,
                handleEditExceptionDescription,
                handleEditServiceContent,
                handleDeleteService,
                handleChangeAttributeValue,
                handleDeleteAttributeValue,
                handleChangeRoomServiceAttributeValue,
                handleDeleteRoomServiceAttributeValue,
                handleDeleteRoomService,
                handleDeleteException,
                handleDownloadExcel,
                handleChangeStatus,
                criticalExceptionLevelType,
                editUrl,
                usedURLPaths,
                suggestedURLs,
                addUrl,
                uploadLogoImageToDDBB,
                uploadIconImageToDDBB,
                handleAddHotelImages,
                handleUpdateHotelImage,
            }}
        >
            {children}
        </ZitoneoContentContext.Provider>
    );
};

export const useZitoneoContent = (): ZitoneoContentContextType => {
    const context = useContext(ZitoneoContentContext);
    if (!context) {
        throw new Error(
            'useZitoneoContent must be used within a ProviderZitoneoContent'
        );
    }
    return context;
};

export const useZitoneoServices = () => {
    const appLanguage = useLanguage();
    const {
        hotelContent,
        serviceCategories,
        serviceTypes,
        hotelImageTypes,
        ...rest
    } = useZitoneoContent();
    const uiContext = useZitoneoUI();
    const { visibleContentScope } = uiContext;
    const {
        HotelServices,
        ZitoneoPublishStatus,
        ZitoneoPublishStatusKey,
        ZitoneoPublishStatusColor,
    } = hotelContent;
    const appLang = appLanguage as AvailableEditionLanguages;

    const ComplementaryServiceCategories =
        serviceCategories[appLang] || serviceCategories.en || [];
    const ComplementaryServiceTypes =
        serviceTypes[appLang] || serviceTypes.en || [];
    const ComplementaryHotelImageTypes =
        hotelImageTypes[appLang] || hotelImageTypes.en || [];

    return {
        HotelName: hotelContent.Name,
        HotelServices: HotelServices.filter(service => !service.Deleted).map(
            service => ({
                ...service,
                AttributeValues: service.AttributeValues?.filter(
                    attributeValue => !attributeValue.Deleted
                ),
                Exceptions: service.Exceptions.filter(
                    exception => !exception.Deleted
                ),
            })
        ),
        ZitoneoPublishStatus,
        ZitoneoPublishStatusKey,
        ZitoneoPublishStatusColor,
        ComplementaryServiceCategories: ComplementaryServiceCategories.filter(
            category =>
                visibleContentScope === VisibleContentScope.ROOM
                    ? category.ForRoom
                    : category.ForHotel
        ),
        ComplementaryServiceTypes: ComplementaryServiceTypes.filter(
            serviceType =>
                visibleContentScope === VisibleContentScope.ROOM
                    ? serviceType.ForRoom
                    : serviceType.ForHotel
        ),
        ComplementaryHotelImageTypes,
        ...uiContext,
        ...rest,
    };
};

export const useZitoneoServicesStats = () => {
    const {
        HotelServices: hotelServices,
        selectedCategory,
        ComplementaryServiceTypes,
        visibleContentScope,
        selectedRoomId,
    } = useZitoneoServices();
    const { hotelContent } = useZitoneoContent();
    const HotelServices: ZinantisServiceState[] =
        visibleContentScope === VisibleContentScope.ROOM
            ? (
                  hotelContent.HotelRoomTypes.find(
                      room => room.Id === selectedRoomId
                  )?.Services || []
              )
                  .filter(service => !service.Deleted)
                  .map(service => ({
                      ...service,
                      Name: '',
                      Exceptions: [],
                  }))
            : hotelServices;
    const servicesInCategory = HotelServices.filter(service =>
        selectedCategory
            ? String(service.IdServiceCategory) === selectedCategory.Id
            : true
    );

    const selectedServices = ComplementaryServiceTypes.filter(serviceType =>
        HotelServices.some(
            hotelService =>
                hotelService.IdServiceType === parseInt(serviceType.Id) &&
                !hotelService.Deleted
        )
    );
    const totalServicesInCategory = selectedCategory
        ? ComplementaryServiceTypes.filter(
              serviceType =>
                  String(serviceType.IdServiceCategory) === selectedCategory.Id
          ).length
        : ComplementaryServiceTypes.length;
    const selectedServicesInCategory = selectedCategory
        ? selectedServices.filter(
              service =>
                  String(service.IdServiceCategory) === selectedCategory.Id
          ).length
        : selectedServices.length;

    const servicesWithMultipleInstancesInCategory = servicesInCategory.reduce(
        (total, service) => {
            const instancesWithSameType = servicesInCategory.filter(
                hotelService =>
                    hotelService.IdServiceType === service.IdServiceType
            ).length;

            return instancesWithSameType > 1
                ? total.add(service.IdServiceType)
                : total;
        },
        new Set<number>()
    );

    const totalAttributeDefinitionsInCategory = servicesInCategory.reduce(
        (total, service) =>
            total +
            (service.AttributeDefinitions
                ? service.AttributeDefinitions.length
                : 0),
        0
    );

    const totalFilledAttributeFilledInCategory = servicesInCategory.reduce(
        (total, service) => {
            if (!service.AttributeDefinitions?.length) {
                return total;
            }

            const filledDefinitions = service.AttributeDefinitions.filter(
                definition => {
                    const matchingValues = (
                        service.AttributeValues || []
                    ).filter(
                        attributeValue =>
                            attributeValue.IdAttributeDefinition ===
                                definition.IdAttributeDefinition &&
                            !attributeValue.Deleted
                    );

                    const hasFilledValue = matchingValues.some(
                        attributeValue => {
                            return (
                                attributeValue.Value !== null &&
                                attributeValue.Value !== undefined &&
                                attributeValue.Value !== ''
                            );
                        }
                    );

                    if (!hasFilledValue) {
                        return false;
                    }

                    // Non-unique definitions can have multiple values, but count as filled once.
                    if (!definition.Unique) {
                        return true;
                    }

                    return true;
                }
            ).length;

            return total + filledDefinitions;
        },
        0
    );

    const requiredAtributesInCategory = servicesInCategory.flatMap(service => {
        if (!service.AttributeDefinitions?.length) {
            return [];
        }

        return service.AttributeDefinitions.filter(
            definition => definition.Required
        );
    });

    const requiredAtributesMissingInCategory = servicesInCategory.flatMap(
        service => {
            if (!service.AttributeDefinitions?.length) {
                return [];
            }

            return service.AttributeDefinitions.filter(definition => {
                if (!definition.Required) {
                    return false;
                }

                const matchingValues = (service.AttributeValues || []).filter(
                    attributeValue =>
                        attributeValue.IdAttributeDefinition ===
                            definition.IdAttributeDefinition &&
                        !attributeValue.Deleted
                );

                const hasFilledValue = matchingValues.some(attributeValue => {
                    return (
                        attributeValue.Value !== null &&
                        attributeValue.Value !== undefined &&
                        attributeValue.Value !== ''
                    );
                });

                return !hasFilledValue;
            }).map(definition => ({
                IdHotelService: service.Id,
                IdServiceType: service.IdServiceType,
                ServiceType: service.ServiceType,
                InstanceName: service.Name || '',
                Definition: definition,
            }));
        }
    ) as RequiredAttributeMissingState[];

    const descriptionsMissingInCategory = servicesInCategory.filter(service => {
        return !service.Description || service.Description.trim() === '';
    });

    const servicesWithActiveExceptionsInCategory = servicesInCategory.filter(
        service =>
            service.Exceptions.some(
                exception => !exception.Deleted && exception.Active
            )
    );

    const servicesWithoutAttributeValuesInCategory = servicesInCategory.filter(
        service =>
            !service.AttributeValues?.length ||
            service.AttributeValues.every(
                attributeValue => attributeValue.Deleted
            ) ||
            service.AttributeValues.every(
                attributeValue =>
                    attributeValue.Value === null ||
                    attributeValue.Value === undefined ||
                    attributeValue.Value === ''
            )
    );

    return {
        totalServices: ComplementaryServiceTypes.length,
        servicesInCategory,
        selectedServices: selectedServices.length,
        totalServicesInCategory,
        selectedServicesInCategory,
        servicesWithMultipleInstancesInCategory:
            servicesWithMultipleInstancesInCategory.size,
        totalAttributeDefinitionsInCategory,
        totalFilledAttributeFilledInCategory,
        requiredAtributesInCategory,
        requiredAtributesMissingInCategory,
        descriptionsMissingInCategory,
        servicesWithActiveExceptionsInCategory,
        servicesWithoutAttributeValuesInCategory,
    };
};

export const useZitoneoSettingsChecks = () => {
    const { hasUnsavedChanges, hotelContent } = useZitoneoContent();

    const { ZitoneoPublishStatusKey, URLs, IdLogoFile, IdIconFile } =
        hotelContent;

    const hasURLs = !!URLs.filter(url => !url.Deleted).length;
    const primaryURL = URLs.find(url => url.IsPrimary && !url.Deleted);
    const hasPrimaryURL = !!primaryURL;
    const primaryIsEnabled = primaryURL?.Enabled ?? false;

    return {
        hasUnsavedChanges,
        published: ZitoneoPublishStatusKey === STATUS_KEYS.PUBLISHED,
        hasURLs,
        hasPrimaryURL,
        primaryIsEnabled,
        primaryURL,
        hasLogo: IdLogoFile > 0,
        hasIcon: IdIconFile > 0,
    };
};

export const useZitoneoPreview = () => {
    const { hotelContent, serviceCategories, languages, hasUnsavedChanges } =
        useZitoneoContent();
    const { visibleLanguage } = useZitoneoUI();
    const langCode = (visibleLanguage?.Code as AvailableEditionLanguages) || '';
    const currentCategories = serviceCategories[langCode] || [];
    const activeServices = hotelContent.HotelServices.filter(
        service => !service.Deleted
    );
    const {
        Id,
        Name,
        HotelOwner,
        Address,
        LastChange,
        LastChangeBy,
        IdHotel,
        GiataCode,
        Rooms,
        ContractType,
        Category,
        CategoryKey,
        HotelImages,
        HotelRoomTypes,
        LogoFile,
    } = hotelContent;

    const adaptServiceCategories = (
        services: ZinantisServiceState[]
    ): ZitoneoPreviewServiceCategoryState[] => {
        return currentCategories
            .filter(category =>
                services.some(
                    service => String(service.IdServiceCategory) === category.Id
                )
            )
            .map(category => ({
                ...category,
                ServiceCount: services.filter(
                    service => String(service.IdServiceCategory) === category.Id
                ).length,
                Services: services
                    .filter(
                        service =>
                            String(service.IdServiceCategory) === category.Id
                    )
                    .map(service => {
                        const translationServiceType =
                            service?.ServiceTypeTranslations?.find(
                                translation =>
                                    translation.IdLanguage ===
                                    parseInt(visibleLanguage?.Id || '')
                            );

                        return {
                            ...service,
                            ServiceType:
                                translationServiceType?.Name ||
                                service.ServiceType,
                            AttributeValues: service.AttributeValues?.filter(
                                attributeValue =>
                                    !attributeValue.Deleted &&
                                    attributeValue.Value !== '' &&
                                    attributeValue.Value !== 'False'
                            ),
                            AttributeDefinitions:
                                service.AttributeDefinitions?.map(
                                    attributeDefinition => {
                                        const translatedName =
                                            attributeDefinition.NameTranslations.find(
                                                translation =>
                                                    translation.IdLanguage ===
                                                    parseInt(
                                                        visibleLanguage?.Id ||
                                                            ''
                                                    )
                                            )?.Name;
                                        return {
                                            ...attributeDefinition,
                                            Name:
                                                translatedName ||
                                                attributeDefinition.Name,
                                        };
                                    }
                                ),
                            Exceptions: service.Exceptions.filter(
                                exception =>
                                    !exception.Deleted && exception.Active
                            ),
                        };
                    }),
            }));
    };

    return {
        hasUnsavedChanges,
        visibleLanguage,
        languages,
        Name,
        HotelOwner,
        Address,
        ContractType: {
            ...ContractType,
            Name:
                ContractType.Translations.find(
                    translation =>
                        translation.IdLanguage ===
                        parseInt(visibleLanguage?.Id || '')
                )?.Name || ContractType.Name,
        },
        LastChange,
        LastChangeBy,
        IdHotel,
        IdSupplierHotel: Id,
        GiataCode,
        Rooms,
        Category,
        CategoryKey,
        ActiveServices: activeServices.length,
        ServiceCategories: adaptServiceCategories(activeServices),
        HotelImages: HotelImages.filter(
            image => !image.Deleted && image.Published
        ),
        HotelRoomTypes: HotelRoomTypes.map(room => ({
            ...room,
            HotelImages: room.HotelImages.filter(
                image => !image.Deleted && image.Published
            ),
            ServiceCategories: adaptServiceCategories(
                room.Services.filter(service => !service.Deleted).map(
                    service => ({
                        ...service,
                        Name: '',
                        Exceptions: [],
                    })
                )
            ),
        })),
        LogoFile,
    };
};

export const useZitoneoSettings = () => {
    const {
        hotelContent,
        editUrl,
        usedURLPaths,
        suggestedURLs,
        addUrl,
        uploadLogoImageToDDBB,
        uploadIconImageToDDBB,
        saveHotelContent,
    } = useZitoneoContent();
    const {
        IdHotel,
        Name,
        URLs,
        ContractType,
        HotelOwner,
        ZitoneoMemberDate,
        ZitoneoMembership,
        ZitoneoPublishStatus,
        ZitoneoPublishStatusKey,
        ZitoneoPublishStatusColor,
        IdLogoFile,
        IdIconFile,
        LogoFile,
        IconFile,
    } = hotelContent;
    return {
        IdHotel,
        HotelName: Name,
        URLs: URLs.filter(url => !url.Deleted),
        ContractType,
        HotelOwner,
        ZitoneoMemberDate,
        ZitoneoMembership,
        ZitoneoPublishStatus,
        ZitoneoPublishStatusKey,
        ZitoneoPublishStatusColor,
        IdLogoFile,
        IdIconFile,
        LogoFile,
        IconFile,
        editUrl,
        usedURLPaths,
        suggestedURLs,
        addUrl,
        uploadLogoImageToDDBB,
        uploadIconImageToDDBB,
        saveHotelContent,
    };
};

export const useZitoneoActivityLogs = () => {
    const { hotelContent } = useZitoneoContent();
    const { ActivityLogs, ZitoneoPublishStatusKey } = hotelContent;

    // Ordenamos los registros de actividad por fecha de creación, de más reciente a más antigua.
    const orderedActivityLogs =
        ActivityLogs?.slice().sort((a, b) => {
            const dateA = a.DateCreated ? new Date(a.DateCreated) : new Date(0);
            const dateB = b.DateCreated ? new Date(b.DateCreated) : new Date(0);
            return dateB.getTime() - dateA.getTime();
        }) || [];

    const agencyStates = getAgencyConnectionStates(orderedActivityLogs);
    const lastConnection = orderedActivityLogs?.[0]?.DateCreated || null;

    return {
        published: ZitoneoPublishStatusKey === STATUS_KEYS.PUBLISHED,
        ActivityLogs: orderedActivityLogs || [],
        LastAgencies: agencyStates.slice(0, 5) || [],
        agenciesCount: agencyStates.length,
        lastConnection,
    };
};

export const useZitoneoHotelRooms = () => {
    const { hotelContent } = useZitoneoContent();
    const { HotelRoomTypes } = hotelContent;

    return {
        rooms:
            HotelRoomTypes.map(room => {
                return {
                    ...room,
                    Services: room.Services.filter(service => !service.Deleted),
                };
            }) || [],
    };
};

const calculateAttributesStatus = (
    services: Array<ZinantisServiceState | ZinantisRoomServiceState>
) => {
    let filled = 0;
    let total = 0;

    services.forEach(service => {
        const definitions = service.AttributeDefinitions || [];
        total += definitions.length;

        definitions.forEach(definition => {
            const hasValue = service.AttributeValues?.some(attributeValue => {
                if (
                    attributeValue.Deleted ||
                    attributeValue.IdAttributeDefinition !==
                        definition.IdAttributeDefinition
                ) {
                    return false;
                }

                return attributeValue.Value.trim() !== '';
            });

            if (hasValue) {
                filled += 1;
            }
        });
    });

    return { value: filled, maxValue: total };
};

/**
 * Calculates hotel presentation completeness statistics.
 * @returns Scalar counts for hotel services, room services, and images.
 */
export const useZitoneoHotelPresentation = () => {
    const {
        hotelContent,
        serviceTypes,
        hotelImageTypes,
        serviceCategories,
        serviceAttributes,
    } = useZitoneoContent();
    const { isLoading } = useZitoneoUI();
    const appLanguage = useLanguage() as AvailableEditionLanguages;

    const hotelServices = hotelContent.HotelServices.filter(
        service => !service.Deleted
    );
    const roomTypes = hotelContent.HotelRoomTypes;
    const roomServices = roomTypes.flatMap(room =>
        room.Services.filter(service => !service.Deleted)
    );

    const localizedServiceTypes =
        serviceTypes[appLanguage] || serviceTypes.en || [];

    const availableHotelServiceTypes = localizedServiceTypes.filter(
        serviceType => serviceType.ForHotel
    );
    const availableRoomServiceTypes = localizedServiceTypes.filter(
        serviceType => serviceType.ForRoom
    );
    const availableHotelImageTypes =
        hotelImageTypes[appLanguage] || hotelImageTypes.en || [];
    const hotelImages = hotelContent.HotelImages.filter(
        image => !image.Deleted
    );
    const { value: filledAttributes, maxValue: totalAttributes } =
        calculateAttributesStatus(hotelServices);
    const { value: filledRoomAttributes, maxValue: totalRoomAttributes } =
        calculateAttributesStatus(roomServices);

    const addedHotelServices = availableHotelServiceTypes.filter(serviceType =>
        hotelServices.some(
            hotelService =>
                hotelService.IdServiceType === parseInt(serviceType.Id)
        )
    ).length;
    const addedRoomServices = availableRoomServiceTypes.filter(serviceType =>
        roomServices.some(
            roomService =>
                roomService.IdServiceType === parseInt(serviceType.Id)
        )
    ).length;
    const totalServiceInstancesDescribed = hotelServices.filter(
        service => !!service.Description.trim()
    ).length;
    const roomsWithServices = roomTypes.filter(room =>
        room.Services.some(service => !service.Deleted)
    ).length;
    const totalRoomServiceInstancesDescribed = roomServices.filter(
        service => service.Description.trim() !== ''
    ).length;
    const coveredImageCategories = new Set(
        hotelImages.map(image => image.IdCategory)
    ).size;
    const roomsWithImages = roomTypes.filter(room =>
        room.HotelImages.some(image => !image.Deleted)
    ).length;

    return {
        isLoading,
        lastChange: hotelContent.LastChange,
        stats: {
            availableHotelServices: availableHotelServiceTypes.length,
            addedHotelServices,
            totalAttributes,
            filledAttributes,
            totalServiceInstances: hotelServices.length,
            totalServiceInstancesDescribed,
            totalRoomTypes: roomTypes.length,
            roomsWithServices,
            availableRoomServices: availableRoomServiceTypes.length,
            addedRoomServices,
            totalRoomAttributes,
            filledRoomAttributes,
            totalRoomServiceInstances: roomServices.length,
            totalRoomServiceInstancesDescribed,
            totalImageCategories: availableHotelImageTypes.length,
            coveredImageCategories,
            roomsWithImages,
        },
        hotel: {
            id: hotelContent.Id,
            idHotel: hotelContent.IdHotel,
            name: hotelContent.Name,
            city: hotelContent.City,
            country: hotelContent.Country,
            hotelOwner: hotelContent.HotelOwner,
            publishStatus: hotelContent.ZitoneoPublishStatus,
            publishStatusKey: hotelContent.ZitoneoPublishStatusKey,
            category: hotelContent.Category,
            categoryKey: hotelContent.CategoryKey,
            address: hotelContent.Address,
            idFirstRoom: hotelContent.HotelRoomTypes?.[0]?.Id,
        },
        serviceCategories:
            serviceCategories[appLanguage]?.filter(
                category => category.ForHotel
            ) || [],
        serviceAttributes: serviceAttributes[appLanguage] || [],
        serviceTypes: localizedServiceTypes,
        hotelServices,
        scrapingResults: hotelContent.ZitoneoScrapingResults,
    };
};
